import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useSubscription } from '../context/SubscriptionContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Check, Zap, Users, Building2, Rocket, ArrowRight, ArrowUp, ArrowDown, AlertTriangle, CreditCard, LogOut, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  {
    id: 'starter',
    name: 'Starter',
    price: 14,
    description: 'Essential tools to get your business running',
    features: [
      'Invoicing & Billing',
      'Client Management',
      'Services & Goods Catalog',
      'Basic Reports',
      'Up to 3 users',
    ],
    icon: Zap,
    color: 'from-blue-500 to-cyan-500',
  },
  {
    id: 'professional',
    name: 'Professional',
    price: 34,
    description: 'Complete team management for growing businesses',
    popular: true,
    features: [
      'Everything in Starter',
      'Employee Management',
      'Equipment & Fleet',
      'Training & Safety',
      'Up to 10 users',
    ],
    icon: Users,
    color: 'from-violet-500 to-purple-500',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: 69,
    description: 'Full-featured solution with payroll',
    features: [
      'Everything in Professional',
      'Payroll & Automation',
      'Union Management',
      'Advanced Analytics',
      'Unlimited users',
    ],
    icon: Building2,
    color: 'from-amber-500 to-orange-500',
  },
];

export default function ManageSubscription() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { subscription, loading: subLoading, refreshSubscription, isAdmin } = useSubscription();
  const [loading, setLoading] = useState(null);
  const [changingPlan, setChangingPlan] = useState(null);

  // Redirect admins to dashboard
  useEffect(() => {
    if (!subLoading && isAdmin) {
      navigate('/dashboard');
    }
  }, [isAdmin, subLoading, navigate]);

  // Redirect users with active subscription who somehow got here
  useEffect(() => {
    if (!subLoading && subscription?.has_subscription && 
        ['active', 'trialing'].includes(subscription?.status) &&
        !subscription?.cancel_at_period_end) {
      // They have active subscription - redirect to dashboard
      // Unless they explicitly came here to manage
    }
  }, [subscription, subLoading]);

  const hasActiveSubscription = subscription?.has_subscription && 
    ['active', 'trialing'].includes(subscription?.status);

  const currentPlan = subscription?.plan;
  const isTrialing = subscription?.status === 'trialing';
  const isCanceling = subscription?.cancel_at_period_end;

  const handleChangePlan = async (planId) => {
    if (!hasActiveSubscription) {
      // No subscription - need to checkout
      handleCheckout(planId);
      return;
    }

    if (planId === currentPlan) {
      toast.info("You're already on this plan");
      return;
    }

    setChangingPlan(planId);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/change-plan?new_plan=${planId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        if (data.proration_message) {
          toast.info(data.proration_message);
        }
        refreshSubscription();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to change plan');
      }
    } catch (error) {
      toast.error('Failed to change plan');
    } finally {
      setChangingPlan(null);
    }
  };

  const handleCheckout = async (planId) => {
    setLoading(planId);
    const token = localStorage.getItem('token');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/checkout`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          plan: planId,
          success_url: `${window.location.origin}/subscription/success`,
          cancel_url: `${window.location.origin}/manage-subscription`,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.checkout_url;
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to start checkout');
      }
    } catch (error) {
      toast.error('Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleOpenBillingPortal = async () => {
    const token = localStorage.getItem('token');
    setLoading('portal');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/portal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        window.location.href = data.portal_url;
      } else {
        const error = await response.json();
        if (error.detail === 'No subscription found') {
          toast.error('No billing information on file. Please select a plan to set up billing.');
        } else {
          toast.error(error.detail || 'Failed to open billing portal');
        }
      }
    } catch (error) {
      toast.error('Failed to open billing portal');
    } finally {
      setLoading(null);
    }
  };

  const handleCancelSubscription = async () => {
    if (!confirm('Are you sure you want to cancel? You will retain access until the end of your billing period.')) {
      return;
    }

    const token = localStorage.getItem('token');
    setLoading('cancel');

    try {
      const response = await fetch(`${API_URL}/api/subscriptions/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        refreshSubscription();
      } else {
        toast.error('Failed to cancel subscription');
      }
    } catch (error) {
      toast.error('Failed to cancel subscription');
    } finally {
      setLoading(null);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (subLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center">
              <Rocket className="h-5 w-5 text-white" />
            </div>
            <span className="text-xl font-bold text-white">Business Genie</span>
          </div>
          <div className="flex items-center gap-4">
            {user && (
              <span className="text-white/70 text-sm hidden sm:block">
                {user.email}
              </span>
            )}
            {hasActiveSubscription && (
              <Button 
                variant="ghost" 
                className="text-white/70 hover:text-white"
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            )}
            <Button 
              variant="ghost" 
              className="text-white/70 hover:text-white"
              onClick={handleLogout}
            >
              <LogOut className="h-4 w-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      {/* Status Banner */}
      {!hasActiveSubscription && (
        <div className="bg-amber-500 text-white px-4 py-3">
          <div className="container mx-auto flex items-center justify-center gap-3">
            <AlertTriangle className="h-5 w-5" />
            <span className="font-medium">
              {subscription?.status === 'canceled' 
                ? 'Your subscription has been canceled. Choose a plan to continue using Business Genie.'
                : subscription?.status === 'past_due'
                ? 'Your payment failed. Please update your payment method or choose a plan.'
                : 'You need an active subscription to access Business Genie. Choose a plan below.'}
            </span>
          </div>
        </div>
      )}

      {/* Current Plan Info (if subscribed) */}
      {hasActiveSubscription && (
        <section className="container mx-auto px-4 pt-8">
          <Card className="bg-slate-800/50 border-slate-700 max-w-2xl mx-auto">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="h-5 w-5" />
                Current Subscription
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-white font-semibold text-lg">
                    {subscription?.plan_name || subscription?.plan} Plan
                  </p>
                  {isTrialing && (
                    <Badge className="bg-blue-500/20 text-blue-400 mt-1">
                      Trial - {subscription?.trial_ends_at ? `Ends ${new Date(subscription.trial_ends_at).toLocaleDateString()}` : 'Active'}
                    </Badge>
                  )}
                  {isCanceling && (
                    <Badge className="bg-red-500/20 text-red-400 mt-1">
                      Canceling - Access until {subscription?.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : 'end of period'}
                    </Badge>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="border-slate-600 text-white hover:bg-slate-700"
                    onClick={handleOpenBillingPortal}
                    disabled={loading === 'portal'}
                  >
                    {loading === 'portal' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Manage Billing'}
                  </Button>
                  {!isCanceling && (
                    <Button
                      variant="ghost"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={handleCancelSubscription}
                      disabled={loading === 'cancel'}
                    >
                      {loading === 'cancel' ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Cancel'}
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* Title */}
      <section className="container mx-auto px-4 pt-12 pb-8 text-center">
        <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">
          {hasActiveSubscription ? 'Change Your Plan' : 'Choose Your Plan'}
        </h1>
        <p className="text-slate-400 max-w-xl mx-auto">
          {hasActiveSubscription 
            ? 'Upgrade or downgrade anytime. Changes are prorated automatically.'
            : 'Select a plan to get started with Business Genie.'}
        </p>
      </section>

      {/* Pricing Cards */}
      <section className="container mx-auto px-4 pb-20">
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {PLANS.map((plan) => {
            const Icon = plan.icon;
            const isCurrentPlan = currentPlan === plan.id;
            const currentPlanPrice = PLANS.find(p => p.id === currentPlan)?.price || 0;
            const isUpgrade = plan.price > currentPlanPrice;
            const isDowngrade = plan.price < currentPlanPrice;
            
            return (
              <Card
                key={plan.id}
                className={`relative bg-slate-800/50 border-slate-700 ${
                  isCurrentPlan ? 'ring-2 ring-green-500' : plan.popular ? 'ring-2 ring-cyan-500' : ''
                }`}
              >
                {isCurrentPlan && (
                  <div className="absolute top-0 right-0 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    CURRENT PLAN
                  </div>
                )}
                {!isCurrentPlan && plan.popular && (
                  <div className="absolute top-0 right-0 bg-cyan-500 text-white text-xs font-bold px-3 py-1 rounded-bl-lg">
                    POPULAR
                  </div>
                )}
                
                <CardHeader className="pb-4">
                  <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${plan.color} flex items-center justify-center mb-3`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <CardTitle className="text-xl text-white">{plan.name}</CardTitle>
                  <CardDescription className="text-slate-400 text-sm">
                    {plan.description}
                  </CardDescription>
                </CardHeader>
                
                <CardContent className="pb-4">
                  <div className="flex items-baseline gap-1 mb-4">
                    <span className="text-3xl font-bold text-white">${plan.price}</span>
                    <span className="text-slate-400 text-sm">/month</span>
                  </div>
                  
                  <ul className="space-y-2">
                    {plan.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm">
                        <Check className="h-4 w-4 text-green-400 flex-shrink-0" />
                        <span className="text-slate-300">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
                
                <CardFooter>
                  {isCurrentPlan ? (
                    <Button disabled className="w-full bg-green-500/20 text-green-400 cursor-default">
                      Current Plan
                    </Button>
                  ) : (
                    <Button
                      className={`w-full ${
                        plan.popular && !hasActiveSubscription
                          ? 'bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-600 hover:to-blue-600'
                          : 'bg-slate-700 hover:bg-slate-600'
                      } text-white`}
                      onClick={() => handleChangePlan(plan.id)}
                      disabled={loading === plan.id || changingPlan === plan.id}
                    >
                      {loading === plan.id || changingPlan === plan.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : hasActiveSubscription ? (
                        <>
                          {isUpgrade ? <ArrowUp className="h-4 w-4 mr-2" /> : <ArrowDown className="h-4 w-4 mr-2" />}
                          {isUpgrade ? 'Upgrade' : 'Downgrade'}
                        </>
                      ) : (
                        <>
                          Get Started
                          <ArrowRight className="h-4 w-4 ml-2" />
                        </>
                      )}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-6">
        <div className="container mx-auto px-4 text-center text-slate-500 text-sm">
          Need help? Contact support@businessgenie.com
        </div>
      </footer>
    </div>
  );
}
